/**
 * O passo Atendimento — o documento DESTE agente (spec 157).
 *
 * **TRÊS GRUPOS, TRÊS DONOS.** "Deste atendimento" é o que você altera e publica
 * (persona, horário, transferência, aviso do orçamento). "Definido pelo operador
 * da plataforma" é deste atendimento, mas só o operador altera: erro ali é ação
 * sem confirmação ou dado entregue à pessoa errada. "Da organização" vale para
 * todos os atendimentos e aparece aqui travado, com o link para a seção certa
 * da configuração da organização (FR-007, FR-008).
 *
 * **O QUE ESTA TELA NÃO DECIDE.** Travar campo aqui é conveniência; quem decide é
 * o servidor, que recusa campo alheio (422 `extra_forbidden`) e campo do operador
 * (403). Erro de validação chega com `loc` e `msg` em português e aparece NO
 * CAMPO, com o Publicar bloqueado enquanto houver erro (FR-010). Quem não pode
 * editar vê tudo em leitura, com o motivo (FR-011).
 *
 * **O HORÁRIO TEM UMA FONTE SÓ** (FR-017): as janelas. A mensagem de fora do
 * horário aceita `{horario}`, a observação é texto complementar, e a prévia que
 * a pessoa lê aqui é a mesma frase que o cliente recebe.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Loader2,
  Lock,
  ShieldAlert,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  descreverHorario,
  errosPorCampo,
  linkDaOrganizacao,
  listaParaTexto,
  podeEditarAtendimento,
  preencherHorario,
  textoParaLista,
  type ErroDeCampo,
  type HorarioDeAtendimento,
} from '@/lib/atendimento'
import { atendimentoService, blueprintService } from '@/services/painel'
import type { BlueprintVersao, Cenario, TimeDoCanal } from '@/schemas/painel'
import { useAuthStore } from '@/stores/auth-store'
import { useOrganizationStore } from '@/stores/organization-store'

interface Props {
  organizationId?: string | null
  organizationName?: string | null
  agentId?: string
}

type No = Record<string, unknown>

function ler(doc: No | null, caminho: string): unknown {
  return caminho
    .split('.')
    .reduce<unknown>((atual, parte) => (atual && typeof atual === 'object' ? (atual as No)[parte] : undefined), doc)
}

function gravar(doc: No, caminho: string, valor: unknown): No {
  const [cabeca, ...resto] = caminho.split('.')
  if (resto.length === 0) return { ...doc, [cabeca]: valor }
  const filho = (doc[cabeca] ?? {}) as No
  return { ...doc, [cabeca]: gravar(filho, resto.join('.'), valor) }
}

const SELECT =
  'flex h-10 w-full rounded-md border border-gray-700/60 bg-white/5 px-3 py-2 text-sm text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 disabled:cursor-not-allowed disabled:opacity-60'

export function StepAtendimento({ organizationId, organizationName, agentId }: Props) {
  const { currentOrganization } = useOrganizationStore()
  const { user } = useAuthStore()
  const podeEditar = podeEditarAtendimento(currentOrganization, user)
  const nomeDaOrganizacao = organizationName ?? currentOrganization?.name ?? 'organização'

  const [doc, setDoc] = useState<No | null>(null)
  const [docDaOrganizacao, setDocDaOrganizacao] = useState<No | null>(null)
  const [versoes, setVersoes] = useState<BlueprintVersao[]>([])
  const [cenarios, setCenarios] = useState<Cenario[]>([])
  const [times, setTimes] = useState<TimeDoCanal[] | null>(null)
  const [naoDividida, setNaoDividida] = useState(false)
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erros, setErros] = useState<ErroDeCampo[]>([])
  const [porCampo, setPorCampo] = useState<Record<string, string>>({})
  const [recusados, setRecusados] = useState<string[]>([])
  const [avisos, setAvisos] = useState<string[]>([])

  const svc = useMemo(() => (agentId ? blueprintService.doAgente(agentId) : null), [agentId])

  const buscar = useCallback(async () => {
    if (!organizationId || !svc) return
    setCarregando(true)
    setNaoDividida(false)
    try {
      const asVersoes = await svc.versoes(organizationId)
      setVersoes(asVersoes)
      const maisRecente = asVersoes[0]
      if (maisRecente) {
        const corpo = await svc.versao(organizationId, maisRecente.version)
        setDoc((corpo.spec ?? corpo) as No)
      } else {
        setDoc(null)
      }
      setCenarios(await svc.cenarios(organizationId).catch(() => []))
      // O que é da organização, para o grupo travado: a versão publicada dela.
      const daOrg = await blueprintService.versoes(organizationId).catch(() => [])
      const publicada = daOrg.find((v) => v.status === 'published')
      if (publicada) {
        const corpo = await blueprintService.versao(organizationId, publicada.version)
        setDocDaOrganizacao((corpo.spec ?? corpo) as No)
      }
      setTimes(await atendimentoService.times(organizationId).catch(() => null))
    } catch (erro) {
      const status = (erro as { response?: { status?: number } }).response?.status
      if (status === 409) {
        setNaoDividida(true)
      } else {
        toast.error('Não foi possível ler o documento deste atendimento.')
      }
    } finally {
      setCarregando(false)
    }
  }, [organizationId, svc])

  useEffect(() => {
    void buscar()
  }, [buscar])

  const vigente = versoes.find((v) => v.status === 'published')
  const rascunho = versoes.find((v) => v.status === 'draft')
  const horario = (ler(doc, 'handoff.horario') as HorarioDeAtendimento | null) ?? null
  const horarioDescrito = descreverHorario(horario)
  const timeEscolhido = String(ler(doc, 'handoff.default_team') ?? '')
  const timeSumiu =
    !!times && !!timeEscolhido && !times.some((t) => t.name.toLowerCase() === timeEscolhido.toLowerCase())
  const errosVisiveis: Record<string, string> = {
    ...porCampo,
    ...(timeSumiu && !porCampo['handoff.default_team']
      ? {
          'handoff.default_team': `O time "${timeEscolhido}" não existe mais na conta do canal; as transferências estão chegando sem time.`,
        }
      : {}),
  }
  const temErro = Object.keys(errosVisiveis).length > 0 || erros.some((e) => e.loc.length === 0)

  const aplicarErros = (detalhe: unknown) => {
    const { porCampo: mapa, lista } = errosPorCampo(detalhe)
    setPorCampo(mapa)
    setErros(lista)
  }

  const mudar = (caminho: string, valor: unknown) => {
    if (!doc) return
    setDoc(gravar(doc, caminho, valor))
    // Mexer no campo (ou num pai dele, como `handoff.horario`) limpa o erro que
    // o servidor pôs ali: a próxima validação diz se ainda está errado.
    const dentro = (chave: string) => chave === caminho || chave.startsWith(`${caminho}.`)
    if (Object.keys(porCampo).some(dentro)) {
      setPorCampo(Object.fromEntries(Object.entries(porCampo).filter(([k]) => !dentro(k))))
      setErros((lista) => lista.filter((e) => !dentro(e.loc.join('.'))))
    }
  }

  const salvarRascunho = async () => {
    if (!organizationId || !svc || !doc) return
    setSalvando(true)
    setRecusados([])
    try {
      const novo = await svc.criarRascunho(organizationId, doc)
      toast.success(`Rascunho v${novo.version} salvo.`)
      await buscar()
    } catch (erro) {
      const resposta = (erro as { response?: { status?: number; data?: { detail?: unknown } } }).response
      const detalhe = resposta?.data?.detail
      if (resposta?.status === 403 && detalhe && typeof detalhe === 'object' && 'campos' in detalhe) {
        setRecusados(((detalhe as { campos?: string[] }).campos ?? []))
        toast.error('O servidor recusou: há campo do operador alterado.')
      } else if (resposta?.status === 422) {
        aplicarErros(detalhe)
        toast.error('Há campo que não pertence a este atendimento.')
      } else {
        toast.error('Não foi possível salvar o rascunho.')
      }
    } finally {
      setSalvando(false)
    }
  }

  const validar = async () => {
    if (!organizationId || !svc || !rascunho) return
    setSalvando(true)
    try {
      const resultado = await svc.validar(organizationId, rascunho.version)
      aplicarErros(resultado.valido ? [] : resultado.detail)
      toast[resultado.valido ? 'success' : 'error'](
        resultado.valido ? 'Documento válido.' : 'O documento tem erros. Veja os campos marcados.'
      )
    } finally {
      setSalvando(false)
    }
  }

  const publicar = async () => {
    if (!organizationId || !svc || !rascunho) return
    setSalvando(true)
    try {
      const publicado = await svc.publicar(organizationId, rascunho.version)
      setAvisos(publicado.warnings ?? [])
      toast.success(`v${rascunho.version} publicada para este atendimento.`)
      await buscar()
    } catch (erro) {
      const resposta = (erro as { response?: { status?: number; data?: { detail?: unknown } } }).response
      aplicarErros(resposta?.data?.detail ?? 'publicação recusada')
      toast.error('A publicação foi recusada.')
    } finally {
      setSalvando(false)
    }
  }

  if (!organizationId || !agentId) {
    return (
      <p className="py-10 text-center text-gray-400">
        Salve o agente e ligue uma caixa de entrada para editar o atendimento dele.
      </p>
    )
  }

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> lendo o documento…
      </div>
    )
  }

  if (naoDividida) {
    return (
      <div className="rounded-xl border border-dashed border-gray-700 p-6">
        <p className="text-gray-200">
          O atendimento desta organização ainda está em documento único.
        </p>
        <p className="mt-2 max-w-xl text-sm text-gray-400">
          A divisão em documentos por atendimento é feita pelo operador da plataforma.
          Até lá, o documento continua sendo editado pela organização, em{' '}
          <Link to={linkDaOrganizacao('atendimentos')} className="text-indigo-300 underline underline-offset-2">
            Configurações › Atendimento
          </Link>
          .
        </p>
      </div>
    )
  }

  if (!doc) {
    return (
      <div className="rounded-xl border border-dashed border-gray-700 p-6 text-center">
        <p className="text-gray-300">Este atendimento ainda não tem documento.</p>
        <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
          O primeiro documento declara como o cliente é reconhecido e quais capacidades o
          assistente tem — é onboarding, e quem o cria é o operador da plataforma. Depois
          dele, persona, horário e mensagens passam a ser seus.
        </p>
      </div>
    )
  }

  const texto = (caminho: string) => String(ler(doc, caminho) ?? '')
  const janelas = horario?.janelas ?? []
  const fora = (ler(doc, 'handoff.fora_de_horario') as No | null) ?? null
  const capacidades = (ler(doc, 'capacidades') as No[] | undefined) ?? []
  const conectoresUsados = new Set(capacidades.map((c) => String(c.conector)))
  const resolvivelPor = ((ler(doc, 'identidade.identificadores') as No[] | undefined) ?? [])
    .map((i) => i.resolvivel_por)
    .filter(Boolean) as string[]
  for (const c of resolvivelPor) conectoresUsados.add(c)
  const conectoresDaOrg = ((docDaOrganizacao?.conectores as No[] | undefined) ?? []).filter((c) =>
    conectoresUsados.has(String(c.key))
  )
  const operadoresDaOrg = (docDaOrganizacao?.operadores as No[] | undefined) ?? []
  const retencao = (docDaOrganizacao?.retencao as No | undefined) ?? {}
  const registro = (docDaOrganizacao?.registro_de_atendimento as No | null | undefined) ?? null

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-medium text-gray-200">Documento deste atendimento</p>
          <p className="text-sm text-gray-400">
            {vigente ? `No ar: v${vigente.version}` : 'Nenhuma versão publicada ainda'}
            {rascunho && ` · rascunho v${rascunho.version} em edição`}
          </p>
        </div>
        {podeEditar && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={salvando} onClick={() => void salvarRascunho()}>
              Salvar rascunho
            </Button>
            <Button variant="outline" size="sm" disabled={salvando || !rascunho} onClick={() => void validar()}>
              Validar
            </Button>
            <Button
              size="sm"
              disabled={salvando || !rascunho || temErro}
              aria-disabled={salvando || !rascunho || temErro}
              onClick={() => void publicar()}
            >
              Publicar{rascunho ? ` v${rascunho.version}` : ''}
            </Button>
          </div>
        )}
      </div>

      {!podeEditar && (
        <div role="status" className="flex items-start gap-3 rounded-xl border border-gray-700/60 bg-white/5 p-4 text-sm text-gray-300">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
          <p>
            Você está vendo este atendimento em leitura: só administradores da {nomeDaOrganizacao}{' '}
            alteram e publicam o documento. Peça a um deles, ou ao operador da plataforma.
          </p>
        </div>
      )}

      {recusados.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/5 p-4">
          <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
          <div className="text-sm text-red-200/90">
            <p>
              <strong>O servidor recusou a gravação.</strong> Estes campos são do operador da
              plataforma:
            </p>
            <ul className="mt-1 list-inside list-disc font-mono text-xs">
              {recusados.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {erros.length > 0 && (
        <div role="alert" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100">
          <p className="font-medium">
            O documento tem {erros.length === 1 ? '1 erro' : `${erros.length} erros`}. Publicar fica
            bloqueado até corrigir.
          </p>
          <ul className="mt-1 list-inside list-disc text-xs">
            {erros.map((e, i) => (
              <li key={`${e.loc.join('.')}-${i}`}>
                {e.loc.length > 0 && <span className="font-mono text-amber-200/80">{e.loc.join('.')}: </span>}
                {e.msg}
              </li>
            ))}
          </ul>
        </div>
      )}

      {avisos.length > 0 && (
        <div role="status" className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-300" />
          <p>{avisos.join(' ')}</p>
        </div>
      )}

      {/* grupo 1 */}
      <section aria-labelledby="g-deste" className="space-y-5 rounded-xl border border-gray-700/50 p-5">
        <div>
          <h2 id="g-deste" className="text-base font-semibold text-gray-100">
            Deste atendimento
          </h2>
          <p className="text-sm text-gray-400">Você altera e publica. Vale só para este atendimento.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo erros={errosVisiveis} caminho="persona.nome" rotulo="Nome do assistente">
            {(m) => (
              <Input id={m.id} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={texto('persona.nome')} onChange={(e) => mudar('persona.nome', e.target.value)} />
            )}
          </Campo>
          <Campo erros={errosVisiveis} caminho="persona.escritorio_nome" rotulo="Nome do negócio, como o cliente conhece">
            {(m) => (
              <Input id={m.id} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={texto('persona.escritorio_nome')} onChange={(e) => mudar('persona.escritorio_nome', e.target.value || null)} />
            )}
          </Campo>
        </div>
        <Campo erros={errosVisiveis} caminho="persona.tom" rotulo="Tom de voz">
          {(m) => (
            <Textarea id={m.id} rows={2} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={texto('persona.tom')} onChange={(e) => mudar('persona.tom', e.target.value)} />
          )}
        </Campo>
        <Campo erros={errosVisiveis} caminho="persona.limites" rotulo="O que o assistente não faz" ajuda="Um limite por linha.">
          {(m) => (
            <Textarea id={m.id} rows={4} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={listaParaTexto(ler(doc, 'persona.limites'))} onChange={(e) => mudar('persona.limites', textoParaLista(e.target.value))} />
          )}
        </Campo>
        <Campo erros={errosVisiveis} caminho="persona.roteiro" rotulo="Roteiro" ajuda="Entra dentro das regras fixas da plataforma (nunca fingir ser humano, nunca inventar), nunca no lugar delas.">
          {(m) => (
            <Textarea id={m.id} rows={7} className="font-mono text-[13px]" aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={texto('persona.roteiro')} onChange={(e) => mudar('persona.roteiro', e.target.value || null)} />
          )}
        </Campo>

        <fieldset className="space-y-3 rounded-lg border border-gray-800 p-4">
          <legend className="px-1 text-sm font-medium text-gray-200">Horário</legend>
          <p className="text-xs text-gray-400">
            As janelas são a única fonte: decidem se a transferência vai para a equipe ou para a
            fila, e o texto que o cliente lê sai delas.
          </p>
          <div className="space-y-2">
            {janelas.map((j, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-2">
                <Campo erros={errosVisiveis} caminho={`handoff.horario.janelas.${i}.dias`} rotulo="Dias (1 = segunda … 7 = domingo)">
                  {(m) => (
                    <Input id={m.id} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={(j.dias ?? []).join(',')} onChange={(e) => mudar(`handoff.horario.janelas.${i}.dias`, e.target.value.split(',').map((d) => Number(d.trim())).filter((d) => !Number.isNaN(d)))} />
                  )}
                </Campo>
                <Campo erros={errosVisiveis} caminho={`handoff.horario.janelas.${i}.de`} rotulo="Início">
                  {(m) => (
                    <Input id={m.id} className="w-24 font-mono" aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={j.de ?? ''} onChange={(e) => mudar(`handoff.horario.janelas.${i}.de`, e.target.value)} />
                  )}
                </Campo>
                <Campo erros={errosVisiveis} caminho={`handoff.horario.janelas.${i}.ate`} rotulo="Fim">
                  {(m) => (
                    <Input id={m.id} className="w-24 font-mono" aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={j.ate ?? ''} onChange={(e) => mudar(`handoff.horario.janelas.${i}.ate`, e.target.value)} />
                  )}
                </Campo>
                <Button type="button" variant="outline" size="sm" disabled={!podeEditar} aria-label={`Remover janela ${i + 1}`} onClick={() => mudar('handoff.horario.janelas', janelas.filter((_, k) => k !== i))}>
                  Remover
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" disabled={!podeEditar} onClick={() => mudar('handoff.horario', { timezone: horario?.timezone ?? 'America/Sao_Paulo', janelas: [...janelas, { dias: [1, 2, 3, 4, 5], de: '08:00', ate: '18:00' }] })}>
              Adicionar janela
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo erros={errosVisiveis} caminho="handoff.horario.timezone" rotulo="Fuso">
              {(m) => (
                <Input id={m.id} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={horario?.timezone ?? ''} onChange={(e) => mudar('handoff.horario', { timezone: e.target.value, janelas })} />
              )}
            </Campo>
            <Campo erros={errosVisiveis} caminho="handoff.observacao_do_horario" rotulo="Observação para o cliente (opcional)" ajuda="A IA recebe junto das janelas; não é fonte do horário.">
              {(m) => (
                <Input id={m.id} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={texto('handoff.observacao_do_horario')} onChange={(e) => mudar('handoff.observacao_do_horario', e.target.value || null)} />
              )}
            </Campo>
          </div>
          <div className="rounded-md bg-white/5 px-3 py-2 text-sm">
            <p className="text-xs text-gray-400">Como a IA responde quando perguntam o horário</p>
            <p className="text-gray-100" data-testid="previa-horario">
              {horarioDescrito
                ? `${horarioDescrito.charAt(0).toUpperCase()}${horarioDescrito.slice(1)}${texto('handoff.observacao_do_horario') ? `, ${texto('handoff.observacao_do_horario')}` : ''}.`
                : 'Sem janela declarada: atende sempre.'}
            </p>
          </div>
        </fieldset>

        <fieldset className="space-y-3 rounded-lg border border-gray-800 p-4">
          <legend className="px-1 text-sm font-medium text-gray-200">Transferência para a equipe</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo erros={errosVisiveis} caminho="handoff.default_team" rotulo="Equipe de triagem" ajuda="Recebe a conversa quando nenhuma regra por motivo se aplica. Lista lida da conta do canal.">
              {(m) =>
                times && times.length > 0 ? (
                  <select id={m.id} className={cn(SELECT, m.erro && 'border-red-500/60')} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={timeEscolhido} onChange={(e) => mudar('handoff.default_team', e.target.value)}>
                    {timeSumiu && <option value={timeEscolhido}>{timeEscolhido} (não existe mais)</option>}
                    {times.map((t) => (
                      <option key={t.id} value={t.name}>
                        {t.name} · {t.members_count === 1 ? '1 pessoa' : `${t.members_count} pessoas`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input id={m.id} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={timeEscolhido} onChange={(e) => mudar('handoff.default_team', e.target.value)} placeholder={times === null ? 'O canal não respondeu; valor gravado mantido' : 'Nenhum time na conta do canal'} />
                )
              }
            </Campo>
            <Campo erros={errosVisiveis} caminho="handoff.resposta_humana_horas" rotulo="Prazo prometido dentro do horário (horas)">
              {(m) => (
                <Input id={m.id} type="number" min={1} inputMode="numeric" aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={texto('handoff.resposta_humana_horas')} onChange={(e) => mudar('handoff.resposta_humana_horas', e.target.value ? Number(e.target.value) : null)} />
              )}
            </Campo>
          </div>
          <Campo
            erros={errosVisiveis}
            caminho="handoff.fora_de_horario.mensagem"
            rotulo={
              fora?.acao === 'fila_com_sla'
                ? `Fora do horário: fila com prazo de ${String(fora.sla_horas ?? '')} horas, e esta mensagem`
                : 'Fora do horário: esta mensagem'
            }
            ajuda={`{horario} vira o horário das janelas. O cliente lê: "${preencherHorario(texto('handoff.fora_de_horario.mensagem'), horario) || '…'}"`}
          >
            {(m) => (
              <Textarea id={m.id} rows={2} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={texto('handoff.fora_de_horario.mensagem')} onChange={(e) => mudar('handoff.fora_de_horario', { ...(fora ?? { acao: 'mensagem_deterministica' }), mensagem: e.target.value || null })} />
            )}
          </Campo>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-gray-300">Pedir avaliação ao encerrar</span>
            <label className="flex items-center gap-2 text-sm text-gray-200">
              <input type="checkbox" className="h-4 w-4 accent-indigo-500" disabled={!podeEditar} checked={!!ler(doc, 'csat.enabled')} onChange={(e) => mudar('csat.enabled', e.target.checked)} />
              {ler(doc, 'csat.enabled') ? 'Ligado' : 'Desligado'}
            </label>
          </div>
          <Campo
            erros={errosVisiveis}
            caminho="orcamento.mensagem"
            rotulo="Aviso ao transferir quando o orçamento do dia acaba"
            ajuda={`Depois deste aviso, a conversa vai para a ${timeEscolhido || 'equipe de triagem'}. O limite é da ${nomeDaOrganizacao}.`}
          >
            {(m) => (
              <>
                <Input id={m.id} aria-describedby={m.descritoPor} aria-invalid={!!m.erro} disabled={!podeEditar} value={texto('orcamento.mensagem')} onChange={(e) => mudar('orcamento', { mensagem: e.target.value })} />
                <Link to={linkDaOrganizacao('orcamento')} className="text-xs text-indigo-300 underline underline-offset-2">
                  Alterar o limite na organização
                </Link>
              </>
            )}
          </Campo>
        </div>
      </section>

      {/* grupo 2 */}
      <section aria-labelledby="g-operador" className="space-y-3 rounded-xl border border-gray-700/50 p-5">
        <div className="flex items-start gap-3">
          <Lock className="mt-1 h-4 w-4 flex-shrink-0 text-gray-400" />
          <div>
            <h2 id="g-operador" className="text-base font-semibold text-gray-100">
              Definido pelo operador da plataforma
            </h2>
            <p className="text-sm text-gray-400">
              Deste atendimento, mas só o operador da plataforma altera: erro aqui é ação sem
              confirmação ou dado entregue à pessoa errada.
            </p>
          </div>
        </div>
        <dl className="divide-y divide-gray-800/60">
          <Travado rotulo="Como reconhece o cliente">
            {((ler(doc, 'identidade.identificadores') as No[] | undefined) ?? []).map((i) => (
              <span key={String(i.key)} className="mr-2">
                {String(i.label ?? i.key)}
                {i.resolvivel_por ? <span className="ml-1 rounded bg-white/10 px-1.5 py-0.5 text-xs">resolvido por {String(i.resolvivel_por)}</span> : null}
              </span>
            ))}
          </Travado>
          <Travado rotulo="Intenções">{chips(((ler(doc, 'intencoes') as No[] | undefined) ?? []).map((i) => String(i.key)))}</Travado>
          <Travado rotulo="Capacidades">{chips(capacidades.map((c) => String(c.key)))}</Travado>
          <Travado rotulo="Objetivos do funil">{chips(((ler(doc, 'objetivos') as No[] | undefined) ?? []).map((o) => String(o.key)))}</Travado>
          <Travado rotulo="Agrupar mensagens">
            espera {Number(ler(doc, 'ingestao.debounce_ms') ?? 5000) / 1000} s · teto {Number(ler(doc, 'ingestao.teto_ms') ?? 25000) / 1000} s
          </Travado>
        </dl>
      </section>

      {/* grupo 3 */}
      <section aria-labelledby="g-org" className="space-y-3 rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <Building2 className="mt-1 h-4 w-4 flex-shrink-0 text-indigo-300" />
            <div>
              <h2 id="g-org" className="text-base font-semibold text-gray-100">
                Da organização {nomeDaOrganizacao}
              </h2>
              <p className="text-sm text-indigo-200/70">
                Vale para todos os atendimentos da {nomeDaOrganizacao}. Aparece aqui para você saber
                com o que este atendimento conta; muda-se na configuração da organização.
              </p>
            </div>
          </div>
          <Link to={linkDaOrganizacao('atendimentos')} className="rounded-md border border-indigo-400/40 px-3 py-1.5 text-sm text-indigo-100 hover:bg-indigo-500/10">
            Abrir configuração da organização
          </Link>
        </div>
        <dl className="divide-y divide-indigo-500/10">
          <DaOrganizacao rotulo="Orçamento do dia" secao="orcamento" ajuda="O teto é um só; o aviso que o cliente lê é o deste atendimento.">
            Ao acabar: aviso e transferência para a equipe de triagem.
          </DaOrganizacao>
          <DaOrganizacao rotulo="Conectores que este atendimento usa" secao="conectores">
            {conectoresDaOrg.length === 0
              ? 'Nenhum conector em uso.'
              : conectoresDaOrg.map((c) => (
                  <span key={String(c.key)} className="mr-2 inline-flex items-center gap-1">
                    <span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs">{String(c.key)}</span>
                    <span className="text-xs uppercase text-indigo-200/70">{String(c.tipo)}</span>
                    {c.auth_ref ? <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-xs text-emerald-200">credencial no cofre</span> : null}
                  </span>
                ))}
          </DaOrganizacao>
          <DaOrganizacao rotulo="Operadores" secao="operadores">
            {operadoresDaOrg.length === 0
              ? 'Nenhum operador cadastrado'
              : `${operadoresDaOrg.length} ${operadoresDaOrg.length === 1 ? 'pessoa fala' : 'pessoas falam'} em nome dos clientes`}
          </DaOrganizacao>
          <DaOrganizacao rotulo="Retenção" secao="retencao">
            mensagens do canal por {String(retencao.channel_messages_dias ?? 60)} dias
            {retencao.anonimizar_context_apos_encerrar === false ? '' : ' · contexto anonimizado ao encerrar'}
          </DaOrganizacao>
          <DaOrganizacao rotulo="Registro de cada atendimento" secao="registro">
            {registro ? `Envia para ${String(registro.conector)}` : 'Não envia'}
          </DaOrganizacao>
        </dl>
      </section>

      {/* trilho */}
      <div className="grid gap-4 md:grid-cols-2">
        <section aria-labelledby="r-versoes" className="space-y-2 rounded-xl border border-gray-800/60 p-4">
          <h3 id="r-versoes" className="text-sm font-semibold text-gray-200">
            Versões deste atendimento
          </h3>
          <ul className="flex flex-wrap gap-2">
            {versoes.map((v) => (
              <li key={v.version} className={cn('flex items-center gap-1 rounded-lg border px-2 py-1 text-xs', v.status === 'published' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' : 'border-gray-800 text-gray-400')}>
                v{v.version}
                {v.status === 'published' && <CheckCircle2 className="h-3 w-3" />}
                <span className="text-gray-500">{v.status === 'published' ? 'no ar' : v.status === 'draft' ? 'rascunho' : 'arquivada'}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">Nada é apagado. Reverter publica uma versão anterior de novo.</p>
        </section>
        <section aria-labelledby="r-cenarios" className="space-y-2 rounded-xl border border-gray-800/60 p-4">
          <h3 id="r-cenarios" className="text-sm font-semibold text-gray-200">
            Cenários · publicar exige todos verdes
          </h3>
          {cenarios.length === 0 ? (
            <p className="text-xs text-gray-500">Nenhum cenário registrado para este atendimento.</p>
          ) : (
            <ul className="divide-y divide-gray-800/50 text-sm">
              {cenarios.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-1.5">
                  <span className="text-gray-300">{c.name}</span>
                  <span className="text-xs text-gray-500">{c.blueprint_version ? `preso à v${c.blueprint_version}` : 'vale em toda versão'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

interface MetaDoCampo {
  id: string
  erro?: string
  descritoPor?: string
}

/**
 * Um campo com rótulo, ajuda junto dele (não em tooltip) e o erro NO campo (FR-010).
 * Fica fora do componente da tela de propósito: componente definido dentro do render
 * é remontado a cada tecla, e o foco some.
 */
function Campo({
  caminho,
  rotulo,
  ajuda,
  erros,
  children,
}: {
  caminho: string
  rotulo: string
  ajuda?: string
  erros: Record<string, string>
  children: (meta: MetaDoCampo) => JSX.Element
}) {
  const erro = erros[caminho]
  const meta: MetaDoCampo = {
    id: `f-${caminho.replace(/\./g, '-')}`,
    erro,
    descritoPor:
      [ajuda ? `${caminho}-ajuda` : null, erro ? `${caminho}-erro` : null]
        .filter(Boolean)
        .join(' ') || undefined,
  }
  return (
    <div className="space-y-1.5" data-campo={caminho}>
      <Label htmlFor={meta.id} className="text-gray-300">
        {rotulo}
      </Label>
      {children(meta)}
      {ajuda && (
        <p id={`${caminho}-ajuda`} className="text-xs text-gray-400">
          {ajuda}
        </p>
      )}
      {erro && (
        <p id={`${caminho}-erro`} role="alert" className="text-xs text-red-300">
          {erro}
        </p>
      )}
    </div>
  )
}

function chips(valores: string[]) {
  if (valores.length === 0) return <span className="text-gray-500">nenhum</span>
  return valores.map((v) => (
    <span key={v} className="mr-1.5 inline-block rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs text-gray-200">
      {v}
    </span>
  ))
}

function Travado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 py-2 sm:grid-cols-[10rem_1fr] sm:items-start sm:gap-3">
      <dt className="text-sm text-gray-300">{rotulo}</dt>
      <dd className="text-sm text-gray-200">
        {children}
        <span className="mt-0.5 block text-xs text-gray-500">Só o operador da plataforma altera</span>
      </dd>
    </div>
  )
}

function DaOrganizacao({
  rotulo,
  secao,
  ajuda,
  children,
}: {
  rotulo: string
  secao: string
  ajuda?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid gap-1 py-2.5 sm:grid-cols-[10rem_1fr] sm:items-start sm:gap-3">
      <dt className="text-sm text-gray-200">{rotulo}</dt>
      <dd className="text-sm text-gray-200">
        {children}
        {ajuda && <p className="mt-0.5 text-xs text-gray-400">{ajuda}</p>}
        <Link to={linkDaOrganizacao(secao)} className="mt-1 block w-fit text-sm text-indigo-200 underline underline-offset-2 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60">
          Alterar na organização
        </Link>
      </dd>
    </div>
  )
}
